import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const SectionComparisonModal = ({
  isOpen,
  currentContent,
  proposedContent,
  sectionTitle,
  onAccept,
  onReject,
  action = "update"
}) => {
  const [activeTab, setActiveTab] = useState("split");

  if (!isOpen) return null;

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const getWordCount = (content) => {
    const text = stripHtml(content || "");
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  // Create diff between current and proposed content
  const createDiff = (oldContent, newContent) => {
    const oldText = stripHtml(oldContent || "");
    const newText = stripHtml(newContent || "");

    // Simple word-based diff
    const oldWords = oldText.split(/\s+/).filter(w => w.length > 0);
    const newWords = newText.split(/\s+/).filter(w => w.length > 0);

    const diff = [];
    let oldIndex = 0;
    let newIndex = 0;

    while (oldIndex < oldWords.length || newIndex < newWords.length) {
      if (oldIndex >= oldWords.length) {
        // Rest are additions
        diff.push({ type: 'add', text: newWords.slice(newIndex).join(' ') });
        break;
      } else if (newIndex >= newWords.length) {
        // Rest are deletions
        diff.push({ type: 'remove', text: oldWords.slice(oldIndex).join(' ') });
        break;
      } else if (oldWords[oldIndex] === newWords[newIndex]) {
        // Same word
        diff.push({ type: 'same', text: oldWords[oldIndex] });
        oldIndex++;
        newIndex++;
      } else {
        // Find next matching word
        let foundMatch = false;

        // Look ahead in new text for old word
        for (let i = newIndex + 1; i < Math.min(newIndex + 10, newWords.length); i++) {
          if (newWords[i] === oldWords[oldIndex]) {
            // Found old word later in new text - treat as addition
            diff.push({ type: 'add', text: newWords.slice(newIndex, i).join(' ') });
            newIndex = i;
            foundMatch = true;
            break;
          }
        }

        if (!foundMatch) {
          // Look ahead in old text for new word
          for (let i = oldIndex + 1; i < Math.min(oldIndex + 10, oldWords.length); i++) {
            if (oldWords[i] === newWords[newIndex]) {
              // Found new word later in old text - treat as removal
              diff.push({ type: 'remove', text: oldWords.slice(oldIndex, i).join(' ') });
              oldIndex = i;
              foundMatch = true;
              break;
            }
          }
        }

        if (!foundMatch) {
          // No match found, treat as change
          diff.push({ type: 'remove', text: oldWords[oldIndex] });
          diff.push({ type: 'add', text: newWords[newIndex] });
          oldIndex++;
          newIndex++;
        }
      }
    }

    return diff;
  };

  const currentWordCount = getWordCount(currentContent);
  const proposedWordCount = getWordCount(proposedContent);
  const wordDifference = proposedWordCount - currentWordCount;

  const renderContent = (content, title) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h4 className="font-medium text-gray-900 mb-3 flex items-center">
        {title}
        <span className="ml-2 text-sm text-gray-500">
          ({getWordCount(content)} words)
        </span>
      </h4>
      <div
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: content || "<p><em>No content</em></p>" }}
      />
    </div>
  );

  const renderDiffView = () => {
    const diff = createDiff(currentContent, proposedContent);

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
          <FontAwesomeIcon icon="code-compare" className="mr-2 text-purple-500" />
          Changes Highlighted
          <span className="ml-2 text-sm text-gray-500">
            ({diff.filter(d => d.type === 'add').length} additions, {diff.filter(d => d.type === 'remove').length} deletions)
          </span>
        </h4>
        <div className="prose prose-sm max-w-none">
          <div className="leading-relaxed">
            {diff.map((item, index) => {
              if (item.type === 'same') {
                return <span key={index}>{item.text} </span>;
              } else if (item.type === 'add') {
                return (
                  <span
                    key={index}
                    className="bg-green-100 text-green-800 px-1 rounded"
                    title="Added by AI"
                  >
                    {item.text}
                  </span>
                );
              } else if (item.type === 'remove') {
                return (
                  <span
                    key={index}
                    className="bg-red-100 text-red-800 px-1 rounded line-through"
                    title="Removed by AI"
                  >
                    {item.text}
                  </span>
                );
              }
              return null;
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <span className="w-3 h-3 bg-green-100 rounded"></span>
                <span className="text-gray-600">Added</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="w-3 h-3 bg-red-100 rounded"></span>
                <span className="text-gray-600">Removed</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="text-gray-600">Unchanged text has no highlight</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Review AI Changes
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Section: {sectionTitle}
              </p>
            </div>
            <button
              onClick={onReject}
              className="text-gray-400 hover:text-gray-600"
            >
              <FontAwesomeIcon icon="times" size="lg" />
            </button>
          </div>

          {/* Stats */}
          <div className="mt-4 flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon="magic" className="text-blue-500" />
              <span className="text-gray-600">
                AI {action === "create" ? "created" : "updated"} content
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon="chart-line" className="text-gray-400" />
              <span className="text-gray-600">
                Word change:
                <span className={`ml-1 font-medium ${
                  wordDifference > 0 ? "text-green-600" :
                  wordDifference < 0 ? "text-red-600" :
                  "text-gray-600"
                }`}>
                  {wordDifference > 0 ? "+" : ""}{wordDifference}
                </span>
              </span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mt-4 flex space-x-1 border-b border-gray-200">
            <button
              onClick={() => setActiveTab("split")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "split"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Side by Side
            </button>
            <button
              onClick={() => setActiveTab("current")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "current"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Current Only
            </button>
            <button
              onClick={() => setActiveTab("proposed")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "proposed"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Proposed Only
            </button>
            <button
              onClick={() => setActiveTab("diff")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "diff"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <FontAwesomeIcon icon="code-compare" className="mr-1" />
              Diff View
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "split" && (
            <div className="grid grid-cols-2 gap-6 h-full">
              {renderContent(currentContent, "Current Content")}
              {renderContent(proposedContent, "Proposed Content")}
            </div>
          )}

          {activeTab === "current" && (
            <div className="h-full">
              {renderContent(currentContent, "Current Content")}
            </div>
          )}

          {activeTab === "proposed" && (
            <div className="h-full">
              {renderContent(proposedContent, "Proposed Content")}
            </div>
          )}

          {activeTab === "diff" && (
            <div className="h-full">
              {renderDiffView()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Review the changes and choose whether to accept or reject them.
              You can always make manual edits after accepting.
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onReject}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FontAwesomeIcon icon="times" className="mr-2" />
                Reject Changes
              </button>
              <button
                onClick={onAccept}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <FontAwesomeIcon icon="check" className="mr-2" />
                Accept Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionComparisonModal;